import { createEventEmitter, EventEmitter } from "./utils";

class Context {
  private _changedEvent: EventEmitter = createEventEmitter();
  private _actionEvent: EventEmitter = createEventEmitter();
  private _localStorageContext: Object = {};
  private _sessionStorageContext: Object = {};
  private _identityKey = "identity";

  private loadCachedContext(context: any): void {
    Object.keys(context).forEach(key => {
      if (!key.startsWith("_")) {
        const value = context[key];
        this.set(key, value);
        this.changedEvent.emit(key, value);
      }
    });
  }

  get changedEvent(): EventEmitter {
    return this._changedEvent;
  }

  get actionEvent(): EventEmitter {
    return this._actionEvent;
  }

  constructor() {
    this.load();
    window.addEventListener("storage", e => {
      if (e.key === "jit-context" && !JSON.parse(e.newValue).identity) {
        this.set(this._identityKey, null);
      }
      if (e.key === "jit-context" && e.newValue !== e.oldValue && JSON.parse(e.newValue).identity) {
        this.set(this._identityKey, JSON.parse(e.newValue).identity);
      }
    });
  }

  hasItems(contexts: { [x: string]: any }): boolean {
    if (!contexts) return true;

    return Object.keys(contexts)
      .map(key => {
        const value = contexts[key];
        if (value === "null") return this.get(key) === null || typeof this.get(key) == "undefined";
        if (value === "!null") return typeof this.get(key) != "undefined" && this.get(key) !== null;
        return this.get(key) === value;
      })
      .reduce((x, y) => x && y, true);
  }

  load(): void {
    const sessionContext = window.sessionStorage.getItem("jit-context");
    const localContext = window.localStorage.getItem("jit-context");

    if (localContext) {
      this.loadCachedContext(JSON.parse(localContext));
    }
    if (sessionContext) {
      this.loadCachedContext(JSON.parse(sessionContext));
    }
  }

  set(key: string, value: any): void {
    const context = this._sessionStorageContext;
    const storage = window.sessionStorage;

    const oldValue = context[key];
    this[key] = value; //save in in-memory Context object for direct access: e.g. appContext.identity.access_token
    context[key] = value; //save in session storage
    this.changedEvent.emit(key, value, oldValue);
    storage.setItem("jit-context", JSON.stringify(context));

    if (key === this._identityKey) {
      //also save identity-access_token in local storage for cross-tab usage
      this._localStorageContext[key] = value;
      window.localStorage.setItem("jit-context", JSON.stringify(this._localStorageContext));
    }
  }

  get(key: string): any {
    return this[key];
  }

  emit(action: any, contexts: any, url: string = null): void {
    const { args, ...rest }: any = contexts;
    rest && Object.keys(rest).forEach(key => this.set(key, rest[key]));
    this.actionEvent.emit(action, args, url);
  }
}

export const context = new Context();
